import { Button, Skeleton } from '@/components/atoms';
import { useUIColors } from '@/core/hooks';
import {
  ReservationCta,
  type AppointmentSlotCta,
  type ReservationSlotCta,
} from '@/core/reservation';
import React from 'react';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import type { UnitDetailsLabels } from '../constants/getPropertyLabels';
import { usePropertyLabels } from '../hooks/usePropertyLabels';

export interface UnitStickyFooterProps {
  reservationCta: ReservationSlotCta;
  appointmentCta: AppointmentSlotCta;
  onReservePress?: () => void;
  onBookPress?: () => void;
  onViewReservation?: () => void;
  /** When true, the Book / View Appointment slot shows a shimmer. */
  isAppointmentLoading?: boolean;
  /** When true, the Reserve / View Reservation slot shows a shimmer. */
  isReservationLoading?: boolean;
  /** When true, Reserve Unit is hidden (another ADHA unit is already reserved, or profile flag is not true). */
  reserveDisabled?: boolean;
  /** When true, the Book Appointment slot is hidden. View Appointment stays. */
  appointmentBookingDisabled?: boolean;
}

const BUTTON_RADIUS = 4;
const BUTTON_HEIGHT = 48;

function getReservationFooterLabel(
  cta: ReservationSlotCta,
  labels: UnitDetailsLabels,
): string {
  if (cta === ReservationCta.VIEW_RESERVATION) {
    return labels.VIEW_RESERVATION;
  }
  return labels.RESERVE_UNIT;
}

function getAppointmentFooterLabel(
  cta: AppointmentSlotCta,
  labels: UnitDetailsLabels,
): string {
  if (cta === ReservationCta.VIEW_APPOINTMENT) {
    return labels.VIEW_APPOINTMENT;
  }
  return labels.BOOK_APPOINTMENT_BUTTON;
}

function StickyFooterBookSlot({
  isAppointmentLoading,
  rightTitle,
  rightIsPrimary,
  onRightPress,
  colors,
}: Readonly<{
  isAppointmentLoading: boolean;
  rightTitle: string;
  rightIsPrimary: boolean;
  onRightPress?: (() => void) | undefined;
  colors: ReturnType<typeof useUIColors>;
}>) {
  if (isAppointmentLoading) {
    return (
      <View style={styles.buttonWrap}>
        <Skeleton height={BUTTON_HEIGHT} borderRadius={BUTTON_RADIUS} />
      </View>
    );
  }

  return (
    <View style={styles.buttonWrap}>
      <Button
        title={rightTitle}
        variant={rightIsPrimary ? 'primary' : 'outline'}
        fullWidth
        onPress={onRightPress}
        style={[
          styles.actionButton,
          !rightIsPrimary
            ? { backgroundColor: colors.backgroundLevel1 }
            : null,
        ]}
        textStyle={styles.actionLabel}
      />
    </View>
  );
}

/**
 * Sticky bottom bar for mobile web unit details.
 * Two slots from `getReservationCtas`: reservation | appointment.
 */
export function UnitStickyFooter({
  reservationCta,
  appointmentCta,
  onReservePress,
  onBookPress,
  onViewReservation,
  isAppointmentLoading = false,
  isReservationLoading = false,
  reserveDisabled = false,
  appointmentBookingDisabled = false,
}: Readonly<UnitStickyFooterProps>) {
  const colors = useUIColors();
  const insets = useSafeAreaInsets();
  const { UNIT_DETAILS: labels } = usePropertyLabels();
  const isReserved = reservationCta === ReservationCta.VIEW_RESERVATION;
  const onLeftPress =
    reservationCta === ReservationCta.VIEW_RESERVATION
      ? onViewReservation
      : onReservePress;
  const hideReserveCta =
    reservationCta === ReservationCta.RESERVE_UNIT && reserveDisabled;
  const hideBookAppointment =
    appointmentCta === ReservationCta.BOOK_APPOINTMENT &&
    appointmentBookingDisabled;
  const rightIsPrimary =
    appointmentCta === ReservationCta.VIEW_APPOINTMENT || isReserved;

  if (hideReserveCta && hideBookAppointment) {
    return null;
  }

  return (
    <View
      style={[
        styles.container,
        {
          backgroundColor: colors.backgroundLevel1,
          borderTopColor: colors.outlineDisabled,
          paddingBottom: 16 + insets.bottom,
        },
      ]}
    >
      {hideReserveCta ? null : (
        <View style={styles.buttonWrap}>
          {isReservationLoading ? (
            <Skeleton height={BUTTON_HEIGHT} borderRadius={BUTTON_RADIUS} />
          ) : (
            <Button
              title={getReservationFooterLabel(reservationCta, labels)}
              variant="outline"
              fullWidth
              onPress={onLeftPress}
              style={[
                styles.actionButton,
                isReserved
                  ? { backgroundColor: 'transparent' }
                  : { backgroundColor: colors.backgroundLevel1 },
              ]}
              textStyle={styles.actionLabel}
            />
          )}
        </View>
      )}
      {hideBookAppointment ? null : (
        <StickyFooterBookSlot
          isAppointmentLoading={isAppointmentLoading}
          rightTitle={getAppointmentFooterLabel(appointmentCta, labels)}
          rightIsPrimary={rightIsPrimary}
          onRightPress={onBookPress}
          colors={colors}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 20,
    paddingTop: 16,
    borderTopWidth: StyleSheet.hairlineWidth,
  },
  buttonWrap: {
    flex: 1,
    minWidth: 0,
  },
  actionButton: {
    borderRadius: BUTTON_RADIUS,
    alignSelf: 'stretch',
  },
  actionLabel: {
    fontSize: 16,
    lineHeight: 16,
    textAlign: 'center',
  },
});

export default UnitStickyFooter;
